/**
 * Global UI sound-effect singleton. Same synth engine as `useSoundKit`, but
 * exposed as import-anywhere helpers so we don't have to thread a hook through
 * every button. First user gesture unlocks the context on iOS/Safari.
 *
 * Opt-out is per-viewer, saved as `sl.sfx` in localStorage.
 */

const KEY = "sl.sfx";

function loadEnabled(): boolean {
  try {
    const v = localStorage.getItem(KEY);
    return v === null ? true : v === "1";
  } catch { return true; }
}

let enabled = loadEnabled();
let ctx: AudioContext | null = null;
let unlocked = false;
let masterGain: GainNode | null = null;

function ensureCtx(): AudioContext | null {
  if (!enabled) return null;
  if (typeof window === "undefined") return null;
  if (!ctx) {
    const AC = window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
    masterGain = ctx.createGain();
    masterGain.gain.value = 0.6;
    masterGain.connect(ctx.destination);
  }
  if (ctx.state === "suspended") void ctx.resume();
  return ctx;
}

function bindUnlock() {
  if (unlocked || typeof window === "undefined") return;
  const unlock = () => { ensureCtx(); unlocked = true; };
  window.addEventListener("pointerdown", unlock, { once: true });
  window.addEventListener("keydown", unlock, { once: true });
  window.addEventListener("touchstart", unlock, { once: true, passive: true });
}
bindUnlock();

export const sfx = {
  isEnabled: () => enabled,
  setEnabled(v: boolean) {
    enabled = v;
    try { localStorage.setItem(KEY, v ? "1" : "0"); } catch { /* noop */ }
  },

  /** Soft, short click for generic buttons (~50ms). */
  tap() {
    const c = ensureCtx(); if (!c || !masterGain) return;
    const t = c.currentTime;
    const osc = c.createOscillator();
    const g = c.createGain();
    osc.type = "triangle";
    osc.frequency.setValueAtTime(1400, t);
    osc.frequency.exponentialRampToValueAtTime(900, t + 0.04);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.05, t + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.06);
    osc.connect(g).connect(masterGain);
    osc.start(t); osc.stop(t + 0.08);
  },

  /** Warmer, deeper thump for play/pause + toggles (~100ms). */
  pop() {
    const c = ensureCtx(); if (!c || !masterGain) return;
    const t = c.currentTime;
    const osc = c.createOscillator();
    const g = c.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(700, t);
    osc.frequency.exponentialRampToValueAtTime(420, t + 0.09);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.08, t + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.11);
    osc.connect(g).connect(masterGain);
    osc.start(t); osc.stop(t + 0.12);
  },

  /** Two-note ascending blip — for likes, successes, achievements. */
  chirp() {
    const c = ensureCtx(); if (!c || !masterGain) return;
    const t = c.currentTime;
    [880, 1320].forEach((freq, i) => {
      const start = t + i * 0.045;
      const osc = c.createOscillator();
      const g = c.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, start);
      g.gain.setValueAtTime(0.0001, start);
      g.gain.exponentialRampToValueAtTime(0.06, start + 0.006);
      g.gain.exponentialRampToValueAtTime(0.0001, start + 0.08);
      osc.connect(g).connect(masterGain!);
      osc.start(start); osc.stop(start + 0.09);
    });
  },

  /** Filtered-noise sweep for modal open / drawer slide (~180ms). */
  swoosh() {
    const c = ensureCtx(); if (!c || !masterGain) return;
    const t = c.currentTime;
    const dur = 0.18;
    const buf = c.createBuffer(1, Math.floor(c.sampleRate * dur), c.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
    const src = c.createBufferSource();
    src.buffer = buf;
    const filter = c.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.setValueAtTime(2400, t);
    filter.frequency.exponentialRampToValueAtTime(500, t + dur);
    filter.Q.value = 0.9;
    const g = c.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.04, t + 0.03);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(filter).connect(g).connect(masterGain);
    src.start(t); src.stop(t + dur + 0.02);
  },

  /** Soft descending — for closes / undos. */
  reverseSwoosh() {
    const c = ensureCtx(); if (!c || !masterGain) return;
    const t = c.currentTime;
    const dur = 0.14;
    const buf = c.createBuffer(1, Math.floor(c.sampleRate * dur), c.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
    const src = c.createBufferSource();
    src.buffer = buf;
    const filter = c.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.setValueAtTime(700, t);
    filter.frequency.exponentialRampToValueAtTime(1800, t + dur);
    filter.Q.value = 0.9;
    const g = c.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.03, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(filter).connect(g).connect(masterGain);
    src.start(t); src.stop(t + dur + 0.02);
  },
};

/** Attach `data-sfx="tap|pop|chirp|swoosh"` on any element for click-to-sound. */
export function bindGlobalClickSfx() {
  if (typeof document === "undefined") return;
  document.addEventListener("click", (e) => {
    const el = (e.target as HTMLElement | null)?.closest("[data-sfx]") as HTMLElement | null;
    if (!el) return;
    const kind = el.dataset.sfx as "tap" | "pop" | "chirp" | "swoosh" | "reverseSwoosh" | undefined;
    if (!kind) return;
    const fn = (sfx as Record<string, () => void>)[kind];
    if (typeof fn === "function") fn();
  }, { passive: true });
}
